import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid';
import Component from 'vue-loading-overlay';
import { toast } from 'vue3-toastify';
import EventHub from '/src/Util/EventHub.js';
import axios from '/src/components/NovaAxios.js';
import localeEn from 'air-datepicker/locale/en';
import AirDatepicker from 'air-datepicker';
import Swal from 'sweetalert2';
import { nextTick } from 'vue';

const buildInspectionChecklist = () => Array.from({ length: 10 }, () => ({ val: '' }));
const buildEmptyApostilleReturn = () => ({
    Name: '',
    Street1: '',
    Street2: '',
    City: '',
    State: '',
    Zip: '',
    Country: ''
});
const buildEmptyCart = () => ({
    Price: 0,
    Tax: 0,
    MapDistance: 0,
    Total: 0,
    SelectedService: '',
    InspectionReportType: 'ChecklistOnly',
    InspectionOccupiedType: 'Occupied',
    InspectionLocationType: 'Residential',
    InspectionDesc: '',
    InspectionCheckList: buildInspectionChecklist(),
    DeliveryItemInfo: [],
    CourierPickupFee: 0,
    StartAddress: '',
    ServiceSpeed: 0,
    NotaryServiceType: 0,
    NumApostilleItems: 0,
    SelectedDate: null,
    CurrentStep: -1,
    ChosenAppointmentSlot: null,
    ApostilleReturn: buildEmptyApostilleReturn(),
});

export const useEditorStore = defineStore('editor', {
    state: () => ({
        OverrideFileUpload: false,
        CheckoutHideUI: false,
        TenantConfig: {},
        MasterFaqTags: [],
        // All blocks get these
        UniversalBaseBlockProps: {
            BGColor: '#fff',
            PaddingTop: 15,
            PaddingBottom: 15,
            Shadow: false,
            UId: '',
            HasPropertyEditor: true,
            FeatureTie: 'None'
        },

        AvailableBlockStyles: {

            "Hero": [
                {
                    CType: "HeroSwiper",
                    Name: "Hero Slider",
                    Desc: 'Multiple images and text, presented as a "feature slide show".',
                },
                {
                    CType: "SimpleIconHero",
                    Name: "Icon Hero",
                    Desc: "Text callout with a configurable icon."
                },

            ],
            "Cards": [
                {
                    CType: "CardsOne",
                    Name: "Cards 1",
                    Desc: "Intro text along with a configurable number of 'cards' to highlight information."
                },
                {
                    CType: "CardsTwo",
                    Name: "Cards 2",
                    Desc: "Intro text along with a configurable number of 'cards' to highlight information."
                }

            ],
            "Feature": [
                {
                    CType: "FeatureTriple",
                    Name: "Feature Triple",
                    Desc: "Information/feature highlight along with 3 configurable images."
                },
                {
                    CType: "FeatureSingle",
                    Name: "Feature Single",
                    Desc: "Information/feature highlight along with 1 configurable image."
                },
                {
                    CType: "FeatureDouble",
                    Name: "Feature Double",
                    Desc: "Information/feature highlight along with 2 configurable images."
                },
                {
                    CType: "ServiceComparison",
                    Name: "Info Table",
                    Desc: "A table with a configurable number of rows and columns. Great for comparison or list tables."
                },
                {
                    CType: "FeatureBlocks",
                    Name: "Feature Blocks",
                    Desc: "A configurable number of information blocks, each with an icon and information text. "
                },



            ],
            "Nuggets": [
                {
                    CType: "SimpleRowNuggets",
                    Name: "Row of Nuggets",
                    Desc: "A configurable number of information/feature nuggets, arranged in a row."
                },
                {
                    CType: "Steps",
                    Name: "Info Steps",
                    Desc: "A configurable number of 'process steps', laid out with a graphical path connecting each step."
                },
                {
                    CType: "FeatureCloud",
                    Name: "Nugget Cloud",
                    Desc: "A configurable number of condensed information/feature nuggets, each with an icon. "
                },
                {
                    CType: "SimpleRowNuggets2",
                    Name: "Row of Nuggets V2",
                    Desc: "A configurable number of condensed information/feature nuggets, arranged in a row."
                },

            ],
            "Functional": [
                {
                    CType: "Checkout",
                    Name: "Checkout",
                    Desc: "E-Commerce module that allows customers to select and pay for services that you have enabled and configured in the Admin Portal. Prices and scheduling is based entirely by Admin Portal settings. This block is not otherwise configurable in this Editor.",
                },
                {
                    CType: "CourierPricing",
                    Name: "Courier Pricing",
                    Desc: "Automatically fills in pricing information for Courier Delivery pricing based on Admin settings. Configurable text for the 'details' section.",
                },
                {
                    CType: "CourierEstimate",
                    Name: "Courier Estimate",
                    Desc: "Allows a customer to enter pick-up and drop-off addresses for a Courier Delivery. Validates Admin defined service area settings and returns a message to the customer letting them know if the addresses can be serviced and what the delivery fee would be, based on Admin set pricing.",

                },
                {
                    CType: "ApostillePricing",
                    Name: "Apostille Pricing",
                    Desc: "Automatically fills in Apostille service pricing, based on Admin settings.",
                },

                {
                    CType: "FAQ",
                    Name: "FAQ",
                    Desc: "Displays Frequently Asked Questions and Answers, for a configurable FAQ category, based on Admin settings. Useful to display a subset of FAQs, for any given page."
                },
                {
                    CType: "FAQ2",
                    Name: "FAQ V2",
                    Desc: "Displays All Frequently Asked Questions and Answers that exist, based on Admin settings (no category filter). Useful for a dedicated FAQ page."
                },
                {
                    CType: "FieldInspectionEstimate",
                    Name: "Inspection Service Area",
                    Desc: "Allows a customer to enter an address for a potential field inspection request. Confirms address is within Admin setting defined service area and calculates a cost estimate, also based on Admin settings.",
                },
                {
                    CType: "InspectionPricing",
                    Name: "Inspection Pricing",
                    Desc: "Automtically fills in Field Inspection pricing information, based on Admin settings.",

                },
                {
                    CType: "InspectionFeatureCloud",
                    Name: "Inspection Feat. Cloud",
                    Desc: "Displays an information nugget cloud, showing all Field Inspection features that are enabled in the Admin settings.",
                },
                {
                    CType: "InspectionFeatures",
                    Name: "Inspection Venues",
                    Desc: "Auomatically displays feature blocks detailing which Field Inspection venues (Commercial/Industrial/Residential) are enabled. Contains configurable details sections.",


                },
                {
                    CType: "ContactForm",
                    Name: "Contact Form",
                    Desc: "Functional contact form that displays any customer contact information that's entered in the Admin settings. Automatically collects contact request details from the customer through a form. Customer contacts are stored for review in the Admin site.",

                },
                {
                    CType: "NotaryQ",
                    Name: "RON Queue",
                    Desc: "Shows the Remote Online Notary queue which tells customers how many notaries are online processing requests and what the approximate wait time is. Works with the Notary Queue feature in the Admin site.",

                },
                {
                    CType: "NotaryPricingTeaser",
                    Name: "Notary Price Teaser",
                    Desc: "Shows a simple price for Remote Online Notary services, based on calculations for a basic RON session as defined in Admin settings.",

                },
                {
                    CType: "NotaryPricing",
                    Name: "Notary Pricing",
                    Desc: "Shows pricing for Remote Online Notarization services, including loan closings (if applicable), based on Admin settings.",

                },
                {
                    CType: "NotaryFeeSchedule",
                    Name: "Notary Fee Schedule",
                    Desc: "Shows a detailed itemized price list for Remote Online Notarizations, based on Admin settings. Optionally allows the addition of extra, configurable rows.",
                },
                {
                    CType: "MobileNotaryFeeSchedule",
                    Name: "Mobile Notary Fee Schedule",
                    Desc: "Shows a detailed itemized price list for Mobile In-Person Notarizations, based on Admin settings. Optionally allows the addition of extra, configurable rows.",

                },
                {
                    CType: "MobileNotaryPricingTeaser",
                    Name: "Mobile Notary Price Teaser",
                    Desc: "Shows simple travel-fee pricing for Mobile In-Person Notary services, as defined in Admin settings.",

                },
                {
                    CType: "MobileNotaryEstimate",
                    Name: "M. Notary Service Area",
                    Desc: "Allows customer to enter a service address for potential Mobile In-Person Notarization. Confirms service address is within Admin defined area and presents a pricing estimate based on travel distance as defined in Admin settings.",
                }

            ],
            "Misc": [
                {
                    CType: "BlankCanvas",
                    Name: "Blank Canvas",
                    Desc: "An empty block. Express yourself!"
                },
                {
                    CType: "CallToAction",
                    Name: "Call To Action",
                    Desc: "A simple 'call to action' type block, including an image, some text and a button."
                },
                {
                    CType: "Quote",
                    Name: "Block Quote",
                    Desc: "An information block, formatted as a block quote.  Header icon and all text is configurable."
                }
            ],
        },

        // Default values, by component type.
        DefaultBlockProps: {
            'Checkout': {
            },
            'BlankCanvas': {
                BGColor: 'rgb(245,247,250)',
                Content: '<div class="text-center">(Nearly) Blank Canvas</div>'
            },
            'HeroSwiper': {
                Slides: [
                    {
                        imgAlt: 'Courier',
                        img: '/images/courier.jpg',
                        title: '<a style="" href="/content/courier" title="Courier">Metro Courier</a>',
                        content:
                            `
                                <p class="leadin">
                                    <span class="text-bold" style="font-size:20px;">Metro Service Area</span>
                                    <br />
                                    <span class="subleadin">
                                        <i class="bi bi-arrow-right" style="color:white;"></i>
                                        Parcels
                                        <i class="bi bi-arrow-right" style="color:white;"></i>
                                        Letters
                                        <i class="bi bi-arrow-right" style="color:white;"></i>
                                        Documents.
                                    </span>
                                </p>
                                <p>
                                    - <span class="text-yellow-5">Online</span> Scheduling. <br />
                                    - <span class="text-yellow-5">Online</span> Payment. <br />
                                    - <span class="text-yellow-5">Online</span> Tracking.<br />
                                </p>

                                <a target="_top" class="slider-content-box-action btn btn-sm btn-primary btn-pointer" href="/content/checkout" title="Order / Track">Ordering &amp; Tracking</a>
                                `,
                    }
                    , {
                        imgAlt: 'Remote Online Notary',
                        img: '/images/notary.jpg',
                        title: `<a style="" href="/content/notary" title="Remote Online Notary">Remote Online Notary<img src="/images/nna.png" style="height:75px !important;float:right"></a>`,
                        content:
                            `
                                <p class="leadin">
                                <span class="text-bold" style="font-size:20px;">Available &amp; Valid Nationally</span><br />
                                <span class="subleadin">
                                    (mobile device or web-cam required)
                                </span>
                            </p>

                            <p>
                                - Fully <span class="text-yellow-5">remote &amp; online</span> ordering and execution. <br />
                                - Documents, Oaths &amp; Acknowledgments<br />
                                - Remote online <span class="text-yellow-5">loan closing</span> signings<br />
                                - Certified, insured &amp; background checked<br />
                                - <span class="text-yellow-5">In-person</span> notaries available in the Austin metro<br />
                            </p>


                            <a target="_top" class="slider-content-box-action btn btn-sm btn-primary btn-pointer" href="/content/checkout" title="Request Notary">Request Notary</a>
                                `,
                    }
                    , {
                        imgAlt: 'Apostille Service',
                        img: '/images/apostille.jpg',
                        title: `<a style="" href="/content/apostille" title="Apostille">Apostille Service</a>`,
                        content:
                            `
                             <p class="leadin">
                                <span class="text-bold" style="font-size:20px;">Full Service Apostille</span><br />
                                <span class="subleadin">
                                    (Texas State Document Authentication)
                                </span>
                            </p>

                            <p>
                                - <span class="text-yellow-5">Full &amp; Rush Service Available</span><br />
                                - On-site runners at Austin Secretary of State<br />
                                - Mail-in or in-person document acceptance
                            </p>

                            <a target="_top" class="slider-content-box-action btn btn-sm btn-primary btn-pointer" href="/content/apostille" title="More Info">More Info</a>
                             `,
                    }
                    , {
                        imgAlt: 'Field Inspections',
                        img: '/images/fieldi.jpg',
                        title: `<a style="" href="/content/fieldinspection" title="Field Inspections">Field Inspections</a>`,
                        content:
                            `
                             <p class="leadin">
                                 <span class="text-bold" style="font-size:20px;">On-Site when you can't be</span><br />
                                 <span class="subleadin">
                                     Commercial &amp; Residential
                                 </span>
                             </p>

                             <p>
                                 - ATX runners are your eyes on the ground<br />
                                 - We go <span class="text-yellow-5">on-site</span> to document your project<br />
                                 - <span class="text-yellow-5">Photo &amp; Video</span> reports available<br />
                                 - <span class="text-yellow-5">Residental &amp; Commercial*</span> inspections provided
                                 <br /><br />
                                 <small>* industrial &amp; agricultural excluded</small>
                             </p>

                             <a target="_top" class="slider-content-box-action btn btn-sm btn-primary btn-pointer" href="/content/fieldinspection" title="More Info">More Info</a>
                            `,
                    }
                ]
            },
            'CardsOne': {
                HighlightColor: 'rgb(245,247,250)',
                Flipped: false,
                Title: `<span>Your partner for all your document &amp; parcel needs in the metro</span>`,
                BgImg: `<img class="img-fluid" src="/unify/assets/svg/illustrations/plane.svg" alt="Background Image">`,
                Cards: [
                    {
                        content:
                            `<div class="mb-3"><span class="bi-envelope fs-2 text-primary"><!-- icon --></span></div><h4 class="text-primary">Courier Services <span class="bi-arrow-right-circle"><!-- icon --></span></h4><p class="mb-0">Pickup &amp; Delivery throughout the Austin metro. Schedule Online.</p>`,
                    },
                    {
                        content:
                            `<div class="mb-3"><span class="bi-envelope fs-2 text-primary"><!-- icon --></span></div><h4 class="text-primary">Courier Services <span class="bi-arrow-right-circle"><!-- icon --></span></h4><p class="mb-0">Pickup &amp; Delivery throughout the Austin metro. Schedule Online.</p>`,
                    },
                    {
                        content:
                            `<div class="mb-3"><span class="bi-envelope fs-2 text-primary"><!-- icon --></span></div><h4 class="text-primary">Courier Services <span class="bi-arrow-right-circle"><!-- icon --></span></h4><p class="mb-0">Pickup &amp; Delivery throughout the Austin metro. Schedule Online.</p>`,
                    },
                    {
                        content:
                            `<div class="mb-3"><span class="bi-envelope fs-2 text-primary"><!-- icon --></span></div><h4 class="text-primary">Courier Services <span class="bi-arrow-right-circle"><!-- icon --></span></h4><p class="mb-0">Pickup &amp; Delivery throughout the Austin metro. Schedule Online.</p>`,
                    }

                ]
            },
            'FeatureTriple': {
                Flipped: false,
                BGColor: `rgba(10,191,83,.075)`,
                Img1: `/images/ordercourier.jpg`,
                Img2: `/images/trackcourier.jpg`,
                Img3: `/images/deliverycourier.jpg`,
                Content: `
                            <div class="mb-5">
                                <h2>Intra-Metro Courier</h2>
                                <p>Our metro Courier service provides pickup and delivery of parcels and documents in the <strong>Austin, Round Rock, Pflugerville, Cedar Park</strong> and surrounding areas.</p>
                            </div>
                            <ul class="list-checked list-checked-bg-primary list-checked-lg">
                                <li class="list-checked-item"> Same Day Pick-Up &amp; Delivery option </li>
                                <li class="list-checked-item"> Online Scheduling</li>
                                <li class="list-checked-item"> Online Tracking</li>
                                <li class="list-checked-item"> No hidden fees, no contracts</li>
                            </ul>
                            <a target="_top" class="btn btn-sm btn-primary btn-pointer d-block mt-9 shadow" href="/content/checkout" title="Request Pickup">Request Pickup</a>
                `,
            },
            'FeatureSingle': {
                Flipped: false,
                Img1: `/images/oath.jpg`,
                Leadin: `Lead-In`,
                Content: `
                  <div class="mb-5">
                            <h2>Online Notarizations</h2>
                            <p>Our Remote Online Notaries, commissioned in Texas, are standing by to notarize your electronic documents online.</p>
                        </div>
                        <!-- List Checked -->
                        <ul class="list-checked list-checked-soft-bg-primary list-checked-lg mb-5">
                            <li class="list-checked-item"><span class="fw-bold">Instant notarizations</span> or future scheduling available</li>
                            <li class="list-checked-item">All you need is a camera &amp; a computer or mobile device</li>
                            <li class="list-checked-item">Notarizations are legally valid <span class="fw-bold">nationwide</span></li>
                            <li class="list-checked-item"><span class="fw-bold">General notarizations, oaths/jurats &amp; loan signings</span></li>
                            <li class="list-checked-item">Agents are NNA certified, background checked &amp; insured</li>
                        </ul>
                        <div class="text-center"><img class="img-fluid" alt="NNA Logo" src="/images/nna.jpg" style="max-width:100px;"></div>
                        <!-- End List Checked --><a target="_top" class="btn btn-sm btn-primary btn-pointer d-block mt-5 shadow" href="/content/checkout" title="Request Notarization">Request Notarization</a>
                `,
            },
            'FeatureDouble': {
                Flipped: false,
                Img1: `/images/austin.jpg`,
                Img2: `/images/certify.jpg`,
                Content: `
                 <div class="mb-5">
                    <h2>Texas Apostille</h2>
                    <p>Do you need a document authenticated for international use? Our Apostille service takes the hassle and guesswork out of getting your documents certified. </p>
                    <p>We have document runners on the ground in Austin TX to ensure prompt delivery and processing by the Texas Secretary Of State.</p>
                    <p>An Apostille is certificate of validation for a government issued document or notarization. These Apostille certificates must be requested through the Secretary of State in Austin, TX.</p>
                </div>
                <!-- List Checked -->
                <ul class="list-checked list-checked-soft-bg-primary list-checked-lg mb-5">
                    <li class="list-checked-item">Document runners stationed downtown Austin provide in-person Apostille service</li>
                    <li class="list-checked-item">Send us your document, we'll get your Apostille from the Secretary of State and send everything back</li>
                    <li class="list-checked-item"><span class="fw-bold">Overnight Express</span> service is available, where and when possible</li>
                    <li class="list-checked-item">Like with all of our services, you can <span class="fw-bold">track your Apostille</span> request progress online</li>
                </ul>
                <!-- End List Checked --><a class="btn btn-primary btn-pointer" href="/content/apostille">Learn more</a>
                `,
            },
            'SimpleRowNuggets': {
                Nuggets: [
                    {
                        content:
                            `
                            <div class="mb-3"><i class="bi-house fs-1"><!--Icon--></i></div>
                            <h5>Residential</h5>
                            <p>Unoccupied Residential Inspections</p>
                            <a class="link link-pointer" href="/content/fieldinspection">Learn more</a>
                            `,
                    },
                    {
                        content:
                            `
                            <div class="mb-3"><i class="bi-basket fs-1"><!--Icon--></i></div>
                            <h5 >Commercial</h5>
                            <p>Retail &amp; Corporate Inspections</p>
                            <a class="link link-pointer" href="/content/fieldinspection">Learn more</a>
                            `,
                    },
                    {
                        content:
                            `
                            <div class="mb-3"><i class="bi-browser-chrome fs-1"><!--Icon--></i></div>
                            <h5>Online Management</h5>
                            <p>Schedule and Review Online</p>
                            <a class="link link-pointer" href="/content/fieldinspection">Learn more</a>
                            `,
                    },
                    {
                        content:
                            `
                            <div class="mb-3"><i class="bi-shield-check fs-1"><!--Icon--></i></div>
                            <h5>Safe &amp; Secure</h5>
                            <p>Background checked and insured</p>
                            <a class="link link-pointer" href="/content/fieldinspection">Learn more</a>
                            `,
                    }

                ]
            },
            'CourierPricing': {
                FeatureTie: 'CourierDelivery',
                HideFlourish: false,
                Leadin: `Parcel &amp; Letter Delivery`,
                StandardPill: `Most Popular`,
                StandardDesc: `<div class="mb-3">
                                    <h4 class="mb-1">Standard</h4>
                                    <p>24 Hour Delivery</p>
                                </div>`,
                StandardFeatures: `<ul class="list-checked list-checked-soft-bg-success fs-4 mb-2">
                                        <li class="list-checked-item">Per Article Pricing</li>
                                        <li class="list-checked-item">Business Drop-Offs</li>
                                        <li class="list-checked-item">Residential Drop-Offs</li>
                                    </ul>`,
                StandardCTALeft: `
                                <span class="fs-5 text-muted d-block">No Contracts.</span>
                                <span class="fs-5 text-muted d-block">No Other Fees.</span>
                            `,
                StandardCTARight: `<a class="btn btn-white" href="/content/checkout">Request Standard Pick-Up</a>`,

                ExpressPill: `Fastest Delivery`,
                ExpressDesc: `<div class="mb-3">
                                    <h4 class="mb-1">
                                    <i class="bi-arrow-up-short text-primary ms-n2"></i>
                                    Express<span class="text-primary">Plus</span>
                                    </h4>
                                    <p>Same Day Delivery*</p>
                                </div>`,
                ExpressFeatures: `<ul class="list-checked list-checked-soft-bg-success fs-4 mb-2">
                                        <li class="list-checked-item">*Delivery by midnight if pick-up before 3pm</li>
                                        <li class="list-checked-item">*Delivery by 5pm if pick-up before 12pm</li>
                                        <li class="list-checked-item">*Business &amp; Residential Drop-Offs</li>
                                    </ul>`,
                ExpressCTALeft: `
                                <span class="fs-5 text-muted d-block">No Contracts.</span>
                                <span class="fs-5 text-muted d-block">No Other Fees.</span>
                            `,
                ExpressCTARight: `<a class="btn btn-primary" href="/content/checkout">Request Express Pick-Up</a>`,

            },
            'CourierEstimate': {
                FeatureTie: 'CourierDelivery',
                BGColor: 'rgb(245,247,250)',
                Content: `
                <h1>Service Area &amp; Delivery Estimate</h1>
                <div class="text-muted">Enter addresses, below, to verify service area and generate a cost estimate</div>
                `,
            },
            'ServiceComparison': {
                RowColor: 'rgba(0, 0, 0, 0)',
                RowColorAlt: '#F5F7FA',
                Header: `<span class="text-muted">Compare Services</span>`,
                Footer: `<span class="small text-muted fst-italic">"Same <span class="text-primary">Business</span> Day" is delivery before 5pm, as long as pick-up occurs by 12:00PM (Noon).<br />"Same <span class="text-primary">Calendar</span> Day" is delivery by 11:59PM, as long as pick-up occurs by 3:00PM.</span>`,
                Cols: ['<span>Service1</span>', '<i class="bi-arrow-up-short text-primary ms-n2 "><!--i--></i>Express<span class= "text-primary">Plus</span>'],
                Rows: [
                    {
                        Desc: 'Feature X',
                        Vals: ['<i class="bi-check-circle text-primary"><!--i--></i>', '<i class="bi-x-circle text-red"><!--i--></i>']
                    },
                    {
                        Desc: 'Feature Y',
                        Vals: ['<i class="bi-x-circle text-red"><!--i--></i>', '<i class="bi-check-circle text-primary"><!--i--></i>']
                    }
                ]
            },
            'CallToAction': {
                Flipped: false,
                ImgContent: `<figure class="mb-7">
                                    <img class="img-fluid" src="/unify/assets/svg/illustrations/plane.svg" alt="Paper Plane" style="width: 10rem;">
                                </figure>`,
                Content: `<h2 class="mb-7">Your stuff, from here to there, FAST!</h2>
                                <a class="link link-pointer btn btn-primary" style="width:50%" href="/content/checkout">Request Courier Pickup</a>`,
            },
            'FAQ': {
                Leadin: 'Frequently Asked Questions',

                FAQs: [],
                Tags: []
            },
            'FAQ2': {
                Leadin: 'Frequently Asked Questions',

                FAQs: [],
                Tags: []
            },
            'ApostillePricing': {
                FeatureTie: 'Apostille',
                Shadows: false,
                LeadIn: '<div class="col-md-12 h2 text-muted mb-10"> Simple Pricing </div>',
                BGColor: 'rgb(245,247,250)',
                Content: `<button type="button" class="btn btn-primary"><i class="bi-envelope"></i> Request Apostille Service</button>`,
            },
            'Quote': {
                InnerColor: 'rgb(245,247,250)',
                Header: 'Did You Know',
                Quote: "'Apostille' is a French term that means 'a certification'",
                Caption: 'An Apostille is what we call an authentication certificate issued under the Hague Convention, which is cooperative agreement between countires related to various legal processes.',
                Footer: 'To date, there are over 120 countries part of this convention.',
                Icon1: {
                    "Show": true,
                    "Icon": 'bi-question-circle',
                    "Color": '#000',
                    "Size": '36'
                }
            },
            'Steps': {
                SequenceColor: '#0abf53',
                CardBgColor: '#fff',
                Leadin: 'Our Service',
                HeaderIcon: {
                    "Show": true,
                    "Icon": 'bi-question-circle',
                    "Color": '#000',
                    "Size": '36'
                },
                Steps: [
                    {
                        HeaderAlignCenter: true,
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-question-circle',
                            "Color": '#000',
                            "Size": '36'
                        },
                        Title: 'Request Free Consultation',
                        Content:
                            `When you <a href="/content/checkout" class="link link-primary">request our apostille service</a>, we begin with a fast and free no-obligation email consultation.<br /><br />We'll respond within a few hours to let you know if your document request can be completed.  <br /><br />Not all types of documents will be accepted by the Secretary of State.`,
                    }

                ]
            },
            'FieldInspectionEstimate': {
                FeatureTie: 'FieldInspection',
                ShowServiceTicks: true,
                Img1: `/images/inspect.jpg`,
                Header: `Field Inspections`,
                Leadin: `Your eyes and ears on location.`,
                Content: `Just because you can't be physically present doesn't mean you need to miss out on critical information. Let our runners go on-site to document and record the state of things for you.`,
            },
            'MobileNotaryEstimate': {
                FeatureTie: 'FieldNotarization',
                Img1: `/images/mobilenotary.jpg`,
                Header: `Mobile In-Person Notary`,
                Leadin: `Traveling Notaries Are On Call!`,
                Content: `When you need a 'wet signature' notarization, we offer mobile notarizations at your location.`,
            },

            'InspectionPricing': {
                FeatureTie: 'FieldInspection',
                BGColor: 'rgb(245,247,250)',
            },
            'FeatureCloud':
            {
                Header: 'Header Text',
                Leadin: `Sub-Text Information'`,
                Nuggets: [
                    {
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-info-circle',
                            "Color": '#000',
                            "Size": '36'
                        },
                        Title: 'Nugget 1',
                        SubTitle: 'Extra Blurb',
                    },
                    {
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-info-circle',
                            "Color": '#000',
                            "Size": '36'
                        },
                        Title: 'Nugget 2',
                        SubTitle: 'Extra Blurb',
                    },
                    {
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-info-circle',
                            "Color": '#000',
                            "Size": '36'
                        },
                        Title: 'Nugget 3',
                        SubTitle: 'Extra Blurb',
                    }
                ]
            },
            'InspectionFeatureCloud':
            {
                FeatureTie: 'FieldInspection',
                Header: 'Inspection Options',
                Leadin: `Don't see what you need? Just ask.`,
                TextColor: '#000',
                IconColor: '#0abf53',
            },
            'FeatureBlocks': {
                HighlightColor: '#f5f7fa',
                Footer: `<span class="small bg-soft-danger"><i class="bi-info-circle"><!--icon--></i> Some interesting words.</span><br /><span class="small bg-soft-danger text-red"><i class="bi-exclamation-circle text-red"><!--icon--></i> Perhaps another tidbit of brilliant information.</span></div>`,
                Blocks: [
                    {
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-info-circle',
                            "Color": '#000',
                            "Size": '36'
                        },
                        Header: `Block Header Text`,
                        SubTitle: `Some sub titble information`,
                        Content: `<h5>Some Examples:</h5><ul class="list-checked list-checked-dark mb-6"><li class="list-checked-item">Property management compliance</li><li class="list-checked-item">Repair completion check</li><li class="list-checked-item">Pool inspections</li><li class="list-checked-item">Landscaping inspections</li><li class="list-checked-item">Real estate previews</li><li class="list-checked-item">Construction progress check</li></ul>`
                    }
                ]
            },
            'InspectionFeatures': {
                FeatureTie: 'FieldInspection',
                HighlightColor: '#f5f7fa',
                TextColor: '#000',
                IconColor: '#f39568',
                Footer: `Footer Content, if any`,
                ResidentialContent: `<h5>Some Examples:</h5>

                                <!-- List -->
                                <ul class="list-checked list-checked-dark mb-6">
                                    <li class="list-checked-item">Property management compliance</li>
                                    <li class="list-checked-item">Repair completion check</li>
                                    <li class="list-checked-item">Pool inspections</li>
                                    <li class="list-checked-item">Landscaping inspections</li>
                                    <li class="list-checked-item">Real estate previews</li>
                                    <li class="list-checked-item">Construction progress check</li>
                                </ul>`,
                CommercialContent: `<h5>Some Examples:</h5>

                                <!-- List -->
                                <ul class="list-checked list-checked-dark mb-6">
                                    <li class="list-checked-item">Damage documentation</li>
                                    <li class="list-checked-item">Repair checks</li>
                                    <li class="list-checked-item">Retail inventory checks</li>
                                    <li class="list-checked-item">Corporate standards compliance</li>
                                    <li class="list-checked-item">Due diligence reviews</li>
                                    <li class="list-checked-item">Construction progress report</li>
                                </ul>`,
                IndustrialContent: `<h5>Some Examples:</h5>

                                <!-- List -->
                                <ul class="list-checked list-checked-dark mb-6">
                                    <li class="list-checked-item">Agricultural Inspections</li>
                                    <li class="list-checked-item">Plant Review</li>
                                    <li class="list-checked-item">Safety Compliance Checks</li>
                                    <li class="list-checked-item">Production Metrics Review</li>                                    
                                </ul>`
            },
            'ContactForm': {
                Flipped: true,
                Img1: `/images/building1.jpg`,
                Leadin: `Send Us A Message`,
            },
            'NotaryQ': {
                FeatureTie: 'RONNotarization',
                ShowFlourish: true,
                Leadin: `Online Remote Notarizations`,
                Headline: `Notarizations don't have to be inconvenient`,
                Footer: `<button type="button" class="btn btn-primary"><i class="bi-browser-chrome"><!--i--></i> Connect With A Notary Now</button>`
            },
            'SimpleIconHero': {
                ShowBackgroundImage: false,
                BackgroundOverlay: 'rgba(255, 255, 255, 0.5)',
                Img1: `/images/building1.jpg`,
                Icon: {
                    "Show": true,
                    "Icon": 'bi-check-circle',
                    "Color": '#0abf53',
                    "Size": '58'
                },
                Header: `Available Nationwide`,
                Subtext: `Remote Electronic Notarizations performed by our state commissioned Notaries are legally recognized in all 50 states.`,
            },
            'CardsTwo': {
                HighlightColor: 'rgba(10,191,83,.075)',
                Flipped: false,
                Title: `<span style="color:rba(243,149,104)">Easy Mode</span> Notarizations`,
                Content: `The Remote Notarization process is designed with you in mind. You just need a few simple things to get started.`,
                Quote: `<span style="font-style: italic;color:#8997a4">Finally! Convenient notarizations at the click of a button.</span>`,
                Footer: `Signing Services At The Speed Of The Internet`,
                Cards: [
                    {
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-file-pdf',
                            "Color": 'rgb(10,191,83)',
                            "Size": '21'
                        },
                        Header: `Step 1`,
                        Content:
                            `Have a PDF version your document(s)`,
                    },
                    {
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-person-badge',
                            "Color": 'rgb(10,191,83)',
                            "Size": '21'
                        },
                        Header: `Step 2`,
                        Content:
                            `Get ready to present a valid government issued photo ID`,
                    },
                    {
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-webcam',
                            "Color": 'rgb(10,191,83)',
                            "Size": '21'
                        },
                        Header: `Step 3`,
                        Content:
                            `Have access to a Webcam or mobile device`,
                    },
                    {
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-people',
                            "Color": 'rgb(10,191,83)',
                            "Size": '21'
                        },
                        Header: `Step 4`,
                        Content:
                            `Connect instantly with our Notaries via the Web`,
                    },



                ]
            },
            'NotaryPricingTeaser': {
                FeatureTie: 'RONNotarization',
                Header: `Simple Pricing`
            },
            'MobileNotaryPricingTeaser': {
                FeatureTie: 'FieldNotarization',
                Header: `In-Person Mobile Notary`,
                Leadin: `Available In Select Areas`
            },
            'NotaryPricing': {
                FeatureTie: 'RONNotarization',
                HideFlourish: false,
                Leadin: `Remote Online Notary Services`,
                StandardPill: `Most Popular`,
                StandardDesc: `<div class="mb-3">
                                    <h4 class="mb-1">General Notary Work</h4>
                                    <p>Remote Online, Instant Delivery</p>
                                </div>`,
                StandardFeatures: `<ul class="list-checked list-checked-soft-bg-success fs-4 mb-2">
                                        <li class="list-checked-item">Legally Valid Nationwide</li>
                                        <li class="list-checked-item">Acknowledgements</li>
                                        <li class="list-checked-item">Jurats/Oaths/Affirmations</li>
                                    </ul>`,
                StandardCTALeft: `
                                <span class="fs-5 text-muted d-block">No Contracts.</span>
                                <span class="fs-5 text-muted d-block">Zero Hassle.</span>
                            `,
                StandardCTARight: `<a class="btn btn-primary" href="/content/checkout">Request Notary</a>`,

                ClosingPill: `Title & Closing Agents`,
                ClosingDesc: `<div class="mb-3">
                                    <h4 class="mb-1">
                                    <i class="bi-arrow-up-short text-primary ms-n2"></i>
                                    Loan Package Closings
                                    </h4>
                                    <p>Instant or Pre-Scheduled</p>
                                </div>`,
                ClosingFeatures: `<ul class="list-checked list-checked-soft-bg-success fs-4 mb-2">
                                        <li class="list-checked-item">Certified Signing Agents</li>
                                        <li class="list-checked-item">Bonded & Insured</li>
                                        <li class="list-checked-item">Legally Compliant</li>
                                    </ul>`,
                ClosingCTALeft: ``,
                ClosingCTARight: `<a class="btn btn-primary" href="/content/checkout">Request Signing Agent</a>`,

            },
            'SimpleRowNuggets2': {
                Header: `Features At a Glance`,
                Subtext: `Notarizations For THIS Century`,
                Nuggets: [
                    {
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-check-circle',
                            "Color": '#0abf53',
                            "Size": '36'
                        },
                        Content:
                            `Desktop or Mobile`,
                    },
                    {
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-check-circle',
                            "Color": '#0abf53',
                            "Size": '36'
                        },
                        Content: `Instant Downloads`,
                    },
                    {
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-check-circle',
                            "Color": '#0abf53',
                            "Size": '36'
                        },
                        Content: `Multiple Signers`,
                    },
                    {
                        Icon: {
                            "Show": true,
                            "Icon": 'bi-check-circle',
                            "Color": '#0abf53',
                            "Size": '36'
                        },
                        Content: `Witness Support`,
                    },

                ]
            },
            'NotaryFeeSchedule': {
                FeatureTie: 'RONNotarization',
                RowColor: 'rgba(0, 0, 0, 0)',
                RowColorAlt: '#F5F7FA',
                Header: `<span class="text-muted">Remote Service Fee Schedule</span>`,
                Footer: `<span class="small text-muted fst-italic">Extra Information</span>`,
                Cols: ['<i class="bi-credit-card text-primary ms-n2 "><!--i--></i> Fee'],
                Rows: [
                    {
                        Desc: 'Item1',
                        Vals: ['$1']
                    },
                ]
            },
            'MobileNotaryFeeSchedule': {
                FeatureTie: 'FieldNotarization',
                RowColor: 'rgba(0, 0, 0, 0)',
                RowColorAlt: '#F5F7FA',
                Header: `<span class="text-muted">In-Person Service Fee Schedule</span>`,
                Footer: `<span class="small text-muted fst-italic">Extra Information</span>`,
                Cols: ['<i class="bi-credit-card text-primary ms-n2 "><!--i--></i> Fee'],
                Rows: [
                    {
                        Desc: 'Item1',
                        Vals: ['$1']
                    },
                    {
                        Desc: 'Item2',
                        Vals: ['$1']
                    }
                ]
            },
        },
        CurrentPageBlockNodes: [], // Loaded from server

        Checkout: {
            Cart: buildEmptyCart(),
            Controller: {},            
            MapCost: 0,
            BackgroundImage: '',
            AvailableAppointments: {},
            AvailableTimeslots: [],
            Daypicker: null,
            SelectedDayText: '',            
            NoAppointmentsFound: false,
            NextButton: {
                IsVisible: false,
                CanGoNext: true,
                Text: "Next",
                Icon: "bi bi-chevron-right"
            },
        }
    }),
    getters: { // Computed       
        GroupedAppointments: function() {
            const groups = [];
            let currentGroup =[];
            for (const appointment of this.Checkout.SelectedDayAppointments) {
                currentGroup.push(appointment);
                if (currentGroup.length === 3) {
                    groups.push(currentGroup);
                    currentGroup = [];
                }
            }
            if(currentGroup.length > 0) {
                groups.push(currentGroup);
            }
            return groups;
        },
        BackgroundImage: function () {
            return `/images/cartbg.jpg`;
        },
        EnabledServices: function () {
            var s = [];
            if (this.TenantConfig.isServiceEnabledRONNotarization) {
                s.push(["RONNotarization", "Remote Online Notarization"])
            }

            if (this.TenantConfig.isServiceEnabledFieldNotarization) {
                s.push(["FieldNotarization", "Mobile Notary (In-Person)"])
            }

            if (this.TenantConfig.isServiceEnabledCourier) {
                s.push(["CourierDelivery", "Courier Delivery"])
            }

            if (this.TenantConfig.isServiceEnabledApostille) {
                s.push(["Apostille", "Apostille / Document Certification"])
            }

            if (this.TenantConfig.isServiceEnabledFieldInspection) {
                s.push(["FieldInspection", "Field Inspection"])
            }  
            return s;
        }
    },
    actions: {
        //IsSameDay: function (date1, date2) {
        //    return date1.getDate() === date2.getDate() &&
        //        date1.getMonth() === date2.getMonth() &&
        //        date1.getFullYear() === date2.getFullYear();
        //},
       
        //GetSelectedServiceFromUrl: function () {
        //    // Get the fragment identifier from the URL (excluding the "#" symbol)
        //    var fragmentIdentifier = window.location.hash.slice(1).toLowerCase();

        //    // Find the tab with a matching ID (case-insensitive)
        //    var selectedService = this.EnabledServices.find(tab => tab[0].toLowerCase() === fragmentIdentifier)?.[0] ?? '';

        //    // If no valid hash is present or the tab doesn't exist, select the first element in the array
        //    if (!selectedService) {
        //        selectedService = "";
        //    }

        //    return selectedService;
        //},
        UpdateCart: function (newStep) {
            var that = this;

            var token = document.querySelectorAll('input[name=__RequestVerificationToken]')[0].value;
            EventHub.$emit("PageLoading", true);

            var cart = { ...this.Checkout.Cart };
            cart.CurrentStep = newStep;
            return axios.postForm('/API/UpdateCart', {
                cart: cart
            }, {
                cache: false,
                headers: {
                    'RequestVerificationToken': token
                }
            }).then(function (response) {
                EventHub.$emit("PageLoading", false);
                if (response.data.success) {
                    var prevStep = that.Checkout.Cart.CurrentStep;
                    that.Checkout.Cart = response.data.cart;
                    if (!that.Checkout.Cart.CurrentStep) {
                        that.Checkout.Cart.SelectedService = "";
                    }
                    
                    if (that.Checkout.Cart.SelectedDate) {
                        that.Checkout.Cart.SelectedDate = new Date(that.Checkout.Cart.SelectedDate);
                    }
                    EventHub.$emit("GoTab", prevStep, that.Checkout.Cart.CurrentStep);     
                    EventHub.$emit("CartLoaded");
                }
                else {
                    that.Checkout.Cart.CurrentStep--;
                    toast.error("There was an error updating your cart. Please refresh the page and try again. " + response.data.msg);
                }
                return response;
            }).catch(function (error) {
                EventHub.$emit("PageLoading", false);
                toast.error("There was an error. Please refresh the page and try again. " + (error? error : ""));
                throw error;
            });
        },
        EmptyCartAndRestart: function () {
            this.Checkout.Cart = buildEmptyCart();
            this.UpdateCart(0).then((response) => {
                if (response?.data?.success) {
                    window.location.href = '/content/checkout';
                }
            }).catch(() => {
                // Errors already surfaced by UpdateCart; no additional handling required.
            });
        },
        IsLoggedInRouter: function (cbLoggedIn, cbNotLoggedIn) {
            var that = this;
            
            var token = document.querySelectorAll('input[name=__RequestVerificationToken]')[0].value;
            EventHub.$emit("PageLoading", true);
            axios.postForm('/API/IsLoggedIn', {
            }, {
                cache: false,
                headers: {
                    'RequestVerificationToken': token
                }
            }).then(function (response) {
                EventHub.$emit("PageLoading", false);
                if (response.data.success) {
                    if (response.data.loggedin && cbLoggedIn) {
                        cbLoggedIn();
                    }
                    else if (!response.data.loggedin && cbNotLoggedIn) {
                        cbNotLoggedIn();
                    }
                }
                else {
                    toast.error("There was an error. Please refresh the page and try again. " + response.data.msg);
                }
            }).catch(function (error) {
                EventHub.$emit("PageLoading", false);
                toast.error("There was an error. Please refresh the page and try again.");
            });
        },
        SetNextStep() {
            this.SetCurrentStep((this.Checkout.Cart.CurrentStep || 0) + 1);
        },
        SetCurrentStep(newIdx) {
            console.log("New Step Index: " + newIdx);

            this.UpdateCart(newIdx);
            //if (newIdx == 3) {
            //    this.IsLoggedInRouter(() => {
            //        // Logged in
            //        EventHub.$emit('OnStepActivated');
            //    }, () => {
            //        // Not logged in
            //        Swal.fire({
            //            title: 'Nice Choice!',
            //            html: `Let's get you logged in or registered so we can keep track of your order.`,
            //            showCancelButton: true,
            //            confirmButtonText: 'Log In / Register',
            //        }).then((result) => {
            //            if (result.isConfirmed) {
            //                var ret = encodeURIComponent(`/content/checkout#${that.Checkout.Cart.SelectedService}&pay=true`);
            //                var relativeURL = `/account/login?ReturnUrl=${ret}`;
            //                window.location.href = relativeURL;
            //            }
            //        });
            //    });
            //}                       
        },
        FormatDateLocal: function (date) {
            if (!date) {
                return "";
            }
            const year = date.getFullYear();
            const month = date.getMonth() + 1; // getMonth() returns 0-11
            const day = date.getDate();

            // Ensure two digits for month and day
            const monthFormatted = month < 10 ? '0' + month : month;
            const dayFormatted = day < 10 ? '0' + day : day;

            return `${year}-${monthFormatted}-${dayFormatted}`;
        },
        CreateAppointmentSlots: function (newAppointmentLengthMins, startBufferMins, endBufferMins) {
            const slotsByDay = {};
            this.Checkout.AvailableTimeslots.forEach(slot => {
                
                const slotStartTime = new Date(slot.startTime);
                const slotEndTime = new Date(slot.endTime);

                const dayKey = this.FormatDateLocal(slotStartTime); // Extracts the date part

                if (!slotsByDay[dayKey]) {
                    slotsByDay[dayKey] = [];
                }

                var currentTime = new Date(slotStartTime.getTime() + startBufferMins * 60000);

                while (currentTime < slotEndTime) {
                    const potentialEndTime = new Date(currentTime.getTime() + newAppointmentLengthMins * 60000);
                    const endTimeWithBuffer = new Date(potentialEndTime.getTime() + endBufferMins * 60000);

                    // Check if the end time with buffer exceeds the segment's end time
                    if (endTimeWithBuffer <= slotEndTime) {
                        slotsByDay[dayKey].push({
                            startTime: currentTime,
                            endTime: potentialEndTime,
                            duration: newAppointmentLengthMins // Duration in minutes
                        });
                    } else {
                        // Break the loop if the end time with buffer exceeds the segment's end time
                        break;
                    }

                    currentTime = new Date(endTimeWithBuffer.getTime());
                }
            });

            return slotsByDay;
        },
        LoadSchedule: function () {

            EventHub.$emit("WizLoading", true);
            
            var that = this;
            var token = document.querySelectorAll('input[name=__RequestVerificationToken]')[0].value;

            var data =
            {
                service: this.Checkout.Cart.SelectedService,
                address1: this.Checkout.Cart.StartAddress,
                deliveries: this.Checkout.Cart.DeliveryItemInfo
            };

            axios.postForm('/API/GetAvailableServiceAppointment', data,
                {
                    cache: false,
                    headers: {
                        'RequestVerificationToken': token,
                    }
                })
                .then(function (response) {

                    if (response.data.success) {
                        that.Checkout.AvailableTimeslots = response.data.openappointments;
                        that.Checkout.AvailableAppointments = that.CreateAppointmentSlots(response.data.newAppointmentLengthMins, 0, 5);
                        that.SetupDayPicker();
                    }
                    else {
                        toast.error("Failed to find any available service appointments. Please try again later." + response.data.msg);
                        that.Checkout.NoAppointmentsFound = true;
                    }

                    EventHub.$emit("WizLoading", false);
                })
                .catch(function (error) {
                    toast.error("Failed to load schedule. Please refresh the page and try again.");
                    EventHub.$emit("WizLoading", false);
                });
        },
        FormatTime(date) {
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const hoursFormatted = hours % 12 || 12;
            return `${String(hoursFormatted).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`;
        },
        SetupDayPicker: function (daysAhead) {
           
            if (this.Checkout.Daypicker) {
                this.Checkout.Daypicker.destroy();
                //this.Checkout.Cart.SelectedDate = this.Checkout.Cart.SelectedDate || null;
                //this.Checkout.Cart.ChosenAppointmentSlot = this.Checkout.Cart.ChosenAppointmentSlot || null;
                this.Checkout.SelectedDayAppointments = [];
            }

            let today = new Date();
            today.setHours(0, 0, 0, 0);

            var earliestDate = today;

            if (this.Checkout.AvailableTimeslots && this.Checkout.AvailableTimeslots.length) {
                //debugger;
                earliestDate = this.Checkout.AvailableTimeslots.reduce((earliest, current) => {
                    const currentDate = new Date(current.startTime);
                    return earliest === null || currentDate < earliest ? currentDate : earliest;
                }, null);
            }

            earliestDate.setHours(0, 0, 0, 0);

            var that = this;
            var daypickerOptions = {
                inline: true,
                locale: localeEn,
                minDate: earliestDate,
                onSelect({ date, formattedDate, datepicker }) {
                    if (!date && that.Checkout.Cart.SelectedDate) {
                        datepicker.selectDate(that.Checkout.Cart.SelectedDate);
                        return;
                    }
                    that.Checkout.Cart.SelectedDate = date;
                    
                    var appt = that.Checkout.Cart.ChosenAppointmentSlot;

                    that.Checkout.Cart.ChosenAppointmentSlot = null;

                    if (appt) {
                        var sd = new Date(appt.StartTime);
                        var ed = new Date(appt.EndTime);
                        
                        for (let key of Object.keys(that.Checkout.AvailableAppointments)) {
                            let appointments = that.Checkout.AvailableAppointments[key];
                            for (let item of appointments) {
                                if (sd.getTime() === item.startTime.getTime() && ed.getTime() === item.endTime.getTime()) {
                                    that.Checkout.Cart.ChosenAppointmentSlot = item;
                                    break;
                                }
                            }
                            if (that.Checkout.Cart.ChosenAppointmentSlot) {
                                break;
                            }
                        }
                    }

                    that.Checkout.SelectedDayText = date ? that.FormatDateLocal(date) : 'Select Appointment Day';
                    that.Checkout.SelectedDayAppointments = date ? that.Checkout.AvailableAppointments[that.Checkout.SelectedDayText] : [];
                },
                onRenderCell({ date, cellType }) {
                    if (cellType === 'day') {
                        if (that.Checkout.AvailableAppointments[that.FormatDateLocal(date)]) {
                            return {
                                classes: 'RedDotDate',
                            }
                        }
                        else {
                            return { disabled: true };
                        }
                    }
                }
            };

            var that = this;
            nextTick().then(() => {
                console.log("Setup Daypicker." + new Date().toString());
                var element = document.querySelector('.air-datepicker.-inline-');
                if (element) {
                    element.remove();
                }
                that.Checkout.Daypicker = new AirDatepicker('#daypicker', daypickerOptions)

                if (that.Checkout.Cart.SelectedDate
                    && that.Checkout.AvailableAppointments[that.FormatDateLocal(that.Checkout.Cart.SelectedDate)]) {
                    that.Checkout.Daypicker.selectDate(new Date(that.Checkout.Cart.SelectedDate));
                }
            });             
        },        
        updateTenantConfig(cfg) {
            this.TenantConfig = cfg;
            var socials = ['facebook', 'instagram', 'youtube', 'twitter','tikTok'];
            // Show list item if TenantConfig[social] is empty
            // If TenantConfig[social] is not empty, Update the value of HREF to the value of
            // of TenantConfig[social]
            socials.forEach(function (social) {
                if (!cfg[social]) {
                    return;
                }
                var listItem = document.getElementById('socials-' + social);
                var link = document.getElementById('socials-' + social + '-link');

                if (listItem && link) {
                    if (cfg[social]) {
                        // If TenantConfig[social] is not empty, update the HREF attribute
                        link.setAttribute('href', cfg[social]);
                        // Show the list item
                        listItem.style.display = 'inline';
                    } else {
                        // If TenantConfig[social] is empty, hide the list item
                        listItem.style.display = 'none';
                    }
                }
            });
            // Copyright
            
            if (this.TenantConfig.copyright) {
                var cr = document.getElementById('site-copyright');
                if (cr) {
                    cr.style.display = 'inline';
                    cr.innerHTML = "&copy; " + this.TenantConfig.copyright;
                }
            }
        },
        deepSpread(obj) {
            var s = JSON.stringify(obj);
            var copy = JSON.parse(s);
            return copy;
        },
        copyElementToArray(targetArray, copyFromArray, copyFromIndex, enforceMaxAmount) {
            
            if (!Array.isArray(targetArray) || copyFromIndex < 0 || copyFromIndex >= copyFromArray.length) {
                // Invalid parameters, return the original targetArray
                return targetArray;
            }

            if (enforceMaxAmount) {
                if (targetArray.length + 1 > enforceMaxAmount) {
                    toast.warning("The maximum number you can have here is " + enforceMaxAmount);
                    return targetArray;
                }
            }           

            // Clone the object from copyFromArray at the specified index with deep spreading
            const clonedObj = this.deepSpread(copyFromArray[copyFromIndex]);

            // Push the cloned object to the end of targetArray
            targetArray.push(clonedObj);

            return targetArray;
        },


        //copyElementToArray(targetArray, copyFromArray, copyFromIndex, enforceMaxAmount) {
        //    if (!Array.isArray(targetArray) || copyFromIndex < 0 || copyFromIndex >= copyFromArray.length) {
        //        // Invalid parameters, return the original targetArray
        //        return targetArray;
        //    }

        //    if (enforceMaxAmount) {
        //        if (targetArray.length + 1 > enforceMaxAmount) {
        //            toast.warning("The maximum number you can have here is " + enforceMaxAmount);
        //            return targetArray;
        //        }
        //    }

        //    // Clone the object from copyFromArray at the specified index
        //    const clonedObj = { ...copyFromArray[copyFromIndex] };

        //    // Push the cloned object to the end of targetArray
        //    targetArray.push(clonedObj);         

        //    return targetArray;
        //},
        
        deleteElementAtIndex(array, index) {
            if (index >= 0 && index < array.length) {
                // Remove the element at the specified index
                array.splice(index, 1);               
            }
            return array;
        },
        shiftElement(array, index, direction) {
            if (index < 0 || index >= array.length || (direction !== 'left' && direction !== 'right')) {
                // Invalid parameters, return the original array
                return array;
            }

            const newIndex = direction === 'left' ? index - 1 : index + 1;

            if (newIndex < 0 || newIndex >= array.length) {
                // Cannot shift further in that direction, return the original array
                return array;
            }

            // Swap the elements
            [array[index], array[newIndex]] = [array[newIndex], array[index]];
           
            return array;
        },
        replaceCurrentBlockNodes(nodes) {
            if (!nodes) {
                return;
            }          

            for (let i = 0; i < nodes.length; i++) {
                var node = nodes[i];

                if (node.blockProps !== undefined) {
                    node.BlockProps = node.blockProps;
                    if (node.BlockProps) {
                        node.BlockProps = JSON.parse(node.BlockProps);
                    }
                    delete node.blockProps;
                }

                if (!node.BlockProps) {
                    node.BlockProps = this.DefaultBlockProps[node.componentType];
                }

                if (node.suid) {
                    node.BlockProps.UId = node.suid;
                    delete node.suid;
                }

                if (!node.BlockProps.UId) {
                    node.BlockProps.UId = uuidv4();
                }

                // Mix in any new base properties with deep spreading
                var props = {
                    ...this.UniversalBaseBlockProps,
                    ...this.DefaultBlockProps[node.componentType],
                    ...node.BlockProps // Spread node.BlockProps with deep spreading
                };

                node.BlockProps = this.deepSpread(props);
                node.ComponentType = node.componentType;
                node.BlockProps.FeatureTie = node.tie;  
                delete node.componentType;
            }
            this.CurrentPageBlockNodes = nodes;
        },

        //replaceCurrentBlockNodes(nodes) {
        //    if (!nodes || !nodes.length) {
        //        return;
        //    }

        //    for (let i = 0; i < nodes.length; i++) {
        //        var node = nodes[i];

        //        if (node.blockProps !== undefined) {
        //            node.BlockProps = node.blockProps;
        //            if (node.BlockProps) {
        //                node.BlockProps = JSON.parse(node.BlockProps);
        //            }
        //            delete node.blockProps;
        //        }

        //        if (!node.BlockProps) {
        //            node.BlockProps = this.DefaultBlockProps[node.componentType];
        //        }

        //        if (node.suid) {
        //            node.BlockProps.UId = node.suid;
        //            delete node.suid;
        //        }

        //        if (!node.BlockProps.UId) {
        //            node.BlockProps.UId = uuidv4();
        //        }

        //        // mix in any new base properties
        //        var props = {
        //            ...this.UniversalBaseBlockProps,
        //            ...this.DefaultBlockProps[node.componentType],
        //            ...node.BlockProps
        //        };

        //        node.BlockProps = props;

        //        node.ComponentType = node.componentType;
        //        delete node.componentType;
        //    }          
        //    this.CurrentPageBlockNodes = nodes;
        //},
        getNodeById(uid)  {
            return this.CurrentPageBlockNodes.find(node => node.BlockProps.UId === uid);
        },
        //addContentBlock(index, componentType) {
        //    var defaultProps = this.DefaultBlockProps[componentType];
        //    if (!defaultProps) {
        //        toast.error("Unknown component type.");
        //        return;
        //    }

        //    var newNode = {
        //        ComponentType: componentType,
        //        BlockProps: {
        //            ...this.UniversalBaseBlockProps,
        //            ...defaultProps
        //        }
        //    };

        //    newNode.BlockProps.UId = uuidv4();
        //    this.CurrentPageBlockNodes.splice(index, 0, newNode);
        //},
        addContentBlock(index, componentType) {
            var defaultProps = this.DefaultBlockProps[componentType];
            if (!defaultProps) {
                toast.error("Unknown component type.");
                return;
            }     

            var newNode = {
                ComponentType: componentType,
                BlockProps: {
                    ...this.UniversalBaseBlockProps,
                    ...defaultProps // Spread defaultProps with deep spreading
                }
            };

            newNode.BlockProps.UId = uuidv4();
            newNode = this.deepSpread(newNode);
            this.CurrentPageBlockNodes.splice(index, 0, newNode);
        },

        moveContentBlockPosition(dat) {
            this.CurrentPageBlockNodes.splice(dat.targetIndex, 0, this.CurrentPageBlockNodes.splice(dat.startIndex, 1)[0]);
        },
        //duplicateContentNode(index) {
        //    var newNode = {
        //        ...this.CurrentPageBlockNodes[index]
        //    };

        //    newNode.BlockProps.UId = uuidv4();
        //    this.CurrentPageBlockNodes.splice(index + 1, 0, newNode);
        //},
        duplicateContentNode(index) {
            var originalNode = this.CurrentPageBlockNodes[index];

            var newNode = {
                ...originalNode,
                BlockProps: {
                    ...originalNode.BlockProps // Spread originalNode.BlockProps with deep spreading
                }
            };

            newNode.BlockProps.UId = uuidv4();
            newNode = this.deepSpread(newNode);
            this.CurrentPageBlockNodes.splice(index + 1, 0, newNode);
        },

        removeContentNode(index) {
            this.CurrentPageBlockNodes.splice(index, 1);
        },
       
        currentPageBlockNodesToJSON() {
            
            var blockdata = [];
            for (let i = 0; i < this.CurrentPageBlockNodes.length; i++) {
                var bp = this.CurrentPageBlockNodes[i].BlockProps;

                if (bp.FAQs) {
                    // Don't send these FAQ datas, they come from the DB, elsewhere.
                    bp = JSON.stringify(bp);
                    bp = JSON.parse(bp);
                    delete bp.FAQs;
                }

                blockdata.push({
                    tie: this.CurrentPageBlockNodes[i].FeatureTie,
                    componentType: this.CurrentPageBlockNodes[i].ComponentType,
                    idx: i,
                    uid: this.CurrentPageBlockNodes[i].BlockProps.UId,
                    blockprops: bp
                });
            }
            return JSON.stringify(blockdata);
        },     
    },
})
